/**
 * Flow map as rows: the data behind the table view (static and interactive)
 * and anything else that lists a map in reading order.
 *
 * Pure and React-free. Titles are resolved to one language here, so a table
 * sorts and renders plain strings; an empty title stays "" and the renderer
 * decides how to show it (labels.untitled).
 */
import { FLOW_NODE_KINDS, readText, type FlowMapDoc, type FlowNodeKind, type FlowTone } from "./model.js";
import { flowOrder } from "./layout/readingOrder.js";

export type FlowSortKey = "order" | "title" | "kind" | "from" | "to" | "steps" | "links";
export type FlowSortDir = "asc" | "desc";

/** One end of an arrow as the table shows it: the node on the other side and the arrow's label. */
export interface FlowTableRef {
  id: string;
  title: string;
  /** The arrow's label text, "" when it has none. */
  label: string;
}

export interface FlowTableRow {
  id: string;
  /** Position in reading order, starting at 1. */
  order: number;
  kind: FlowNodeKind;
  tone?: FlowTone;
  title: string;
  description: string;
  /** The parent group's title, "" when the node is not in a group (or the group is untitled). */
  group: string;
  /** The parent group's id; unset when the node is not in a group (an untitled group still has one). */
  groupId?: string;
  from: FlowTableRef[];
  to: FlowTableRef[];
  stepsDone: number;
  stepsTotal: number;
  links: number;
}

export type FlowTableColumnLabel = "colOrder" | "colTitle" | "colKind" | "colFrom" | "colTo" | "colSteps" | "colLinks";

export const FLOW_TABLE_COLUMNS: ReadonlyArray<{ key: FlowSortKey; label: FlowTableColumnLabel }> = [
  { key: "order", label: "colOrder" },
  { key: "title", label: "colTitle" },
  { key: "kind", label: "colKind" },
  { key: "from", label: "colFrom" },
  { key: "to", label: "colTo" },
  { key: "steps", label: "colSteps" },
  { key: "links", label: "colLinks" },
];

/**
 * The tone a node's kind chip is drawn in: the node's own tone, else the kind's
 * default (the same defaults the node card uses in css.ts).
 */
export function kindTone(kind: FlowNodeKind, tone?: FlowTone): FlowTone {
  if (tone) return tone;
  switch (kind) {
    case "start":
      return "success";
    case "decision":
    case "note":
      return "warning";
    case "step":
      return "primary";
    default:
      return "neutral";
  }
}

/** Every node as a row, in reading order (see layout.ts `flowOrder`). */
export function flowTableRows(doc: FlowMapDoc, locale?: string): FlowTableRow[] {
  const order = flowOrder(doc);
  const rank = new Map(order.map((id, i) => [id, i] as const));
  const byId = new Map(doc.nodes.map((n) => [n.id, n] as const));
  const titleOf = (id: string): string => readText(byId.get(id)?.title, locale);
  const from = new Map<string, FlowTableRef[]>();
  const to = new Map<string, FlowTableRef[]>();
  const push = (map: Map<string, FlowTableRef[]>, key: string, ref: FlowTableRef): void => {
    const list = map.get(key);
    if (list) list.push(ref);
    else map.set(key, [ref]);
  };
  for (const e of doc.edges) {
    if (!byId.has(e.source) || !byId.has(e.target)) continue;
    const label = readText(e.label, locale);
    push(to, e.source, { id: e.target, title: titleOf(e.target), label });
    push(from, e.target, { id: e.source, title: titleOf(e.source), label });
  }
  const byRank = (a: FlowTableRef, b: FlowTableRef): number => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0);

  const rows: FlowTableRow[] = [];
  order.forEach((id, i) => {
    const n = byId.get(id);
    if (!n) return;
    const steps = n.steps ?? [];
    const parent = n.parentId ? byId.get(n.parentId) : undefined;
    const row: FlowTableRow = {
      id,
      order: i + 1,
      kind: n.kind,
      title: readText(n.title, locale),
      description: readText(n.description, locale),
      group: parent ? readText(parent.title, locale) : "",
      from: (from.get(id) ?? []).sort(byRank),
      to: (to.get(id) ?? []).sort(byRank),
      stepsDone: steps.filter((s) => s.done).length,
      stepsTotal: steps.length,
      links: n.links?.length ?? 0,
    };
    if (n.tone) row.tone = n.tone;
    if (parent) row.groupId = parent.id;
    rows.push(row);
  });
  return rows;
}

const COLLATOR_OPTIONS: Intl.CollatorOptions = { sensitivity: "base", numeric: true };

/** A collator for `locale`; a malformed locale tag falls back to the runtime default instead of throwing. */
function collatorFor(locale: string | undefined): Intl.Collator {
  try {
    return new Intl.Collator(locale, COLLATOR_OPTIONS);
  } catch {
    return new Intl.Collator(undefined, COLLATOR_OPTIONS);
  }
}

/**
 * Sort rows by one column. Stable: equal keys keep reading order. Titles
 * compare as words in `locale` (numbers inside them numerically), kinds in the
 * flow's own order (start, step, decision, end, note, group), and the arrow,
 * step and link columns by their counts.
 */
export function sortFlowRows(rows: readonly FlowTableRow[], key: FlowSortKey, dir: FlowSortDir, locale?: string): FlowTableRow[] {
  const collator = collatorFor(locale);
  const kindRank = (k: FlowNodeKind): number => FLOW_NODE_KINDS.indexOf(k);
  const primary = (a: FlowTableRow, b: FlowTableRow): number => {
    switch (key) {
      case "title":
        return collator.compare(a.title, b.title);
      case "kind":
        return kindRank(a.kind) - kindRank(b.kind);
      case "from":
        return a.from.length - b.from.length;
      case "to":
        return a.to.length - b.to.length;
      case "steps":
        return a.stepsTotal - b.stepsTotal || a.stepsDone - b.stepsDone;
      case "links":
        return a.links - b.links;
      default:
        return a.order - b.order;
    }
  };
  const sign = dir === "desc" ? -1 : 1;
  return rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => sign * primary(a.row, b.row) || a.row.order - b.row.order || a.i - b.i)
    .map((x) => x.row);
}
